import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { Plus, Pencil, Trash2, ImageOff, Loader2, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../lib/api';
import PhotoUploader from './PhotoUploader';

const CATEGORIES = [
  'Appartement',
  'Maison',
  'Rénovation intérieure',
  'Rénovation énergétique',
  'Ravalement / Façade',
  'Copropriété',
];

const EMPTY_FORM = {
  title: '',
  category: CATEGORIES[0],
  location: '',
  description: '',
  published: true,
  before: [],
  during: [],
  after: [],
};

const toPhotoItems = (arr) =>
  (arr || []).map((p) => ({ id: p.id, url: `${getApiUrl()}${p.url}` }));

const AdminRealisations = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const API_URL = getApiUrl();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/realisations`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setItems(data.realisations || []);
      } else {
        toast.error('Erreur lors du chargement des réalisations');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      category: item.category,
      location: item.location || '',
      description: item.description || '',
      published: item.published,
      before: toPhotoItems(item.photos?.before),
      during: toPhotoItems(item.photos?.during),
      after: toPhotoItems(item.photos?.after),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      category: form.category,
      location: form.location.trim(),
      description: form.description.trim(),
      published: form.published,
      photos: {
        before: form.before.map((p) => p.id),
        during: form.during.map((p) => p.id),
        after: form.after.map((p) => p.id),
      },
      cover_file_id:
        form.after[0]?.id || form.during[0]?.id || form.before[0]?.id || null,
    };
    try {
      const url = editingId
        ? `${API_URL}/api/admin/realisations/${editingId}`
        : `${API_URL}/api/admin/realisations`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(editingId ? 'Réalisation mise à jour' : 'Réalisation ajoutée');
        closeForm();
        fetchItems();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/realisations/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Réalisation supprimée');
        fetchItems();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-realisations">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Réalisations</h2>
          <p className="text-sm text-slate-600">Gérez les chantiers affichés sur la page publique.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchItems} className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" /> Actualiser
          </Button>
          <Button
            onClick={openCreate}
            data-testid="add-realisation-btn"
            className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> Ajouter une réalisation
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-slate-600">Chargement…</p>
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ImageOff className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600">Aucune réalisation pour le moment. Ajoutez votre premier chantier.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => (
            <Card key={item.id} data-testid={`realisation-card-${item.id}`} className="overflow-hidden">
              <div className="h-40 bg-slate-100 overflow-hidden relative">
                {item.cover_url ? (
                  <img src={`${API_URL}${item.cover_url}`} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageOff className="h-8 w-8 text-slate-300" />
                  </div>
                )}
                {!item.published && (
                  <Badge className="absolute top-2 left-2 bg-slate-700 text-white">Brouillon</Badge>
                )}
              </div>
              <CardContent className="p-4">
                <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">{item.category}</span>
                <h3 className="font-bold text-slate-900 mt-1 truncate">{item.title}</h3>
                <p className="text-sm text-slate-500 truncate">{item.location || '—'}</p>
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(item)}
                    data-testid={`edit-realisation-${item.id}`}
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    <Pencil className="h-4 w-4" /> Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteTarget(item)}
                    data-testid={`delete-realisation-${item.id}`}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={closeForm}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{editingId ? 'Modifier la réalisation' : 'Nouvelle réalisation'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={closeForm}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Titre *</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex. Rénovation complète d'un appartement haussmannien"
                  data-testid="realisation-title-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Catégorie</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="realisation-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 mb-1 block">Lieu</label>
                  <Input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Ex. Paris 8e"
                    data-testid="realisation-location-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1 block">Description</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Décrivez le chantier, les travaux réalisés…"
                  data-testid="realisation-description-input"
                />
              </div>
              <PhotoUploader
                label="Photos AVANT"
                photos={form.before}
                onChange={(p) => setForm({ ...form, before: p })}
                testId="uploader-before"
              />
              <PhotoUploader
                label="Photos PENDANT"
                photos={form.during}
                onChange={(p) => setForm({ ...form, during: p })}
                testId="uploader-during"
              />
              <PhotoUploader
                label="Photos APRÈS"
                photos={form.after}
                onChange={(p) => setForm({ ...form, after: p })}
                testId="uploader-after"
              />
              <div className="flex items-center justify-between border-t pt-4">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Publier sur le site</p>
                  <p className="text-xs text-slate-500">Si désactivé, reste en brouillon (non visible).</p>
                </div>
                <Switch
                  checked={form.published}
                  onCheckedChange={(v) => setForm({ ...form, published: v })}
                  data-testid="realisation-published-switch"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={closeForm} className="flex-1">Annuler</Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-realisation-btn"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingId ? 'Enregistrer' : 'Ajouter'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-realisation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette réalisation ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? `« ${deleteTarget.title} » sera définitivement supprimée du site. Cette action est irréversible.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
              data-testid="delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminRealisations;
